/**
 * R-70 — Document provenance anchoring: doc + page + char-range bindings.
 */
import { documentBodyText, pageTextFromDocument } from '../documentBody';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { buildEvidenceCacheFromSed, type EvidenceCache } from './providerPrecedenceVerifier';
import { reportFullText } from './reportTextExtract';
import { parseDocCitationUrl, type DocAnchor as ParsedDocAnchor } from './citationIntegrityVerifier';

export interface DocAnchor extends ParsedDocAnchor {
  fileName?: string;
  snippet?: string;
}

export interface ClaimAnchorBinding {
  claimText: string;
  docId?: string;
  fileName?: string;
  anchor?: DocAnchor;
  lineNumber: number;
  sectionId?: string;
  bindMethod: 'doc-url' | 'at-doc-tag' | 'per-filename' | 'unbound';
}

export type ClaimProvenanceKind =
  | 'PHANTOM_DOC'
  | 'UNANCHORED_DOC_CLAIM'
  | 'ORPHAN_CHAR_RANGE'
  | 'SNIPPET_MISMATCH'
  | 'MISSING_DOC_CITATION';

export interface ClaimProvenanceFinding {
  kind: ClaimProvenanceKind;
  severity: 'block' | 'warn';
  docId?: string;
  fileName?: string;
  lineNumber: number;
  message: string;
  claimText?: string;
  anchor?: DocAnchor;
  expectedSnippet?: string;
}

export interface DocProvenanceEvidence extends EvidenceCache {
  uploadedDocs: SiteDocument[];
  anchors: DocAnchor[];
}

const DOC_URL_RE = /doc:\/\/[^\s)\]"']+/gi;
const AT_DOC_RE = /@doc\(([^)]+)\)/gi;
const PER_FILENAME_RE = /\(per\s+"([^"]+)"\)/gi;

export function normalizeDocPages(doc: SiteDocument): Array<{
  page: number;
  charStart: number;
  charEnd: number;
  text: string;
}> {
  if (doc.pages?.length) return doc.pages.map(page => ({ ...page, text: pageTextFromDocument(doc, page) }));
  const text = documentBodyText(doc);
  return [{ page: 1, charStart: 0, charEnd: text.length, text }];
}

/** Build doc-level page anchors from uploaded documents. */
export function extractDocAnchors(
  evidence: { uploadedDocs?: SiteDocument[]; documents?: SiteDocument[] },
): DocAnchor[] {
  const docs = evidence.uploadedDocs ?? evidence.documents ?? [];
  const anchors: DocAnchor[] = [];
  for (const doc of docs) {
    for (const p of normalizeDocPages(doc)) {
      anchors.push({
        docId: doc.id,
        fileName: doc.fileName,
        page: p.page,
        charStart: p.charStart,
        charEnd: p.charEnd,
        snippet: p.text.slice(0, 200),
      });
    }
  }
  return anchors;
}

export function buildDocProvenanceEvidence(
  documents: SiteDocument[],
  sed?: SiteEvidenceDocument | null,
): DocProvenanceEvidence {
  const base = buildEvidenceCacheFromSed(sed);
  const uploadedDocs = documents ?? [];
  return {
    ...base,
    uploadedDocs,
    anchors: extractDocAnchors({ uploadedDocs }),
  };
}

const FILE_EXTENSION_RE = /\.(?:pdf|docx?|xlsx?|pptx?|txt|md|csv|png|jpe?g|tiff?)$/iu;
const MONTH_NUMBER: Readonly<Record<string, number>> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

interface FilenameIdentity {
  base: string;
  dateKey?: string;
}

function fourDigitYear(token: string): number | null {
  if (!/^\d{4}$/u.test(token)) return null;
  const year = Number(token);
  return year >= 1900 && year <= 2200 ? year : null;
}

function boundedNumber(token: string, min: number, max: number): number | null {
  if (!/^\d{1,2}$/u.test(token)) return null;
  const value = Number(token);
  return value >= min && value <= max ? value : null;
}

/**
 * Normalize a human-readable document title without erasing substantive
 * identifiers. Generated reports commonly restyle punctuation, expand `Ind.`
 * to `Industrial`, or cite a dated upload as `(Mar 2026)` instead of
 * `2026.3.4`. Those are filename aliases, not new documents.
 *
 * Date components are removed only when they form a recognizable date. This
 * deliberately preserves numbers such as `1803`, `Phase 1`, and model IDs.
 */
function filenameIdentity(value: string): FilenameIdentity {
  const tokens = value
    .normalize('NFKD')
    .replace(FILE_EXTENSION_RE, '')
    .toLowerCase()
    .replace(/&/gu, ' and ')
    .replace(/[^a-z0-9]+/gu, ' ')
    .trim()
    .split(/\s+/u)
    .filter(Boolean)
    .map((token) => token === 'ind' ? 'industrial' : token);

  const dateIndexes = new Set<number>();
  let dateKey: string | undefined;
  const setDate = (year: number, month: number, indexes: number[]) => {
    dateKey ??= `${year}-${String(month).padStart(2, '0')}`;
    indexes.forEach((index) => dateIndexes.add(index));
  };

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index]!;
    const year = fourDigitYear(token);
    const namedMonth = MONTH_NUMBER[token];

    if (namedMonth) {
      const nextYear = fourDigitYear(tokens[index + 1] ?? '');
      const previousYear = fourDigitYear(tokens[index - 1] ?? '');
      if (nextYear) setDate(nextYear, namedMonth, [index, index + 1]);
      else if (previousYear) setDate(previousYear, namedMonth, [index - 1, index]);
      continue;
    }

    if (year) {
      const month = boundedNumber(tokens[index + 1] ?? '', 1, 12);
      const day = boundedNumber(tokens[index + 2] ?? '', 1, 31);
      if (month) setDate(year, month, day ? [index, index + 1, index + 2] : [index, index + 1]);
      continue;
    }

    const month = boundedNumber(token, 1, 12);
    const day = boundedNumber(tokens[index + 1] ?? '', 1, 31);
    const trailingYear = fourDigitYear(tokens[index + 2] ?? '');
    const adjacentYear = fourDigitYear(tokens[index + 1] ?? '');
    if (month && day && trailingYear) setDate(trailingYear, month, [index, index + 1, index + 2]);
    else if (month && adjacentYear) setDate(adjacentYear, month, [index, index + 1]);
  }

  return {
    base: tokens.filter((_token, index) => !dateIndexes.has(index)).join(' '),
    dateKey,
  };
}

function sameFilenameIdentity(a: string, b: string): boolean {
  const left = filenameIdentity(a);
  const right = filenameIdentity(b);
  if (!left.base || left.base !== right.base) return false;
  // When both titles specify a date, a different month remains a real
  // mismatch. If either title omits the date, the identical substantive title
  // is still a safe filename alias for existence checking.
  return !(left.dateKey && right.dateKey && left.dateKey !== right.dateKey);
}

function matchDocByFilename(fn: string, docs: SiteDocument[]): SiteDocument | null {
  if (fn.length < 4) return null;
  const fnLower = fn.toLowerCase();
  const exact = docs.find((d) => d.fileName.toLowerCase() === fnLower);
  if (exact) return exact;
  const contains = docs.find((d) => d.fileName.toLowerCase().includes(fnLower));
  if (contains) return contains;
  const reverse = docs.find((d) => {
    const docName = d.fileName.toLowerCase();
    return docName.length >= 4 && fnLower.includes(docName);
  });
  if (reverse) return reverse;
  return docs.find((d) => sameFilenameIdentity(fn, d.fileName)) ?? null;
}

function sliceAtAnchor(doc: SiteDocument, anchor: DocAnchor): string {
  // Anchors address the untrimmed captured body, independently of the site's
  // metadata preview. Inline legacy documents use the same maintained fallback.
  const text = documentBodyText(doc);
  const start = Math.max(0, Math.min(anchor.charStart, text.length));
  const end = Math.max(start, Math.min(anchor.charEnd, text.length));
  return text.slice(start, end);
}

function tokenOverlap(a: string, b: string): number {
  const wordsA = new Set(a.toLowerCase().split(/\W+/).filter((w) => w.length > 3));
  const wordsB = new Set(b.toLowerCase().split(/\W+/).filter((w) => w.length > 3));
  if (wordsA.size === 0 || wordsB.size === 0) return 0;
  let overlap = 0;
  for (const w of wordsA) {
    if (wordsB.has(w)) overlap += 1;
  }
  return overlap / Math.min(wordsA.size, wordsB.size);
}

/** Scan report narrative for doc-derived claims and anchor bindings. */
export function findDocDerivedClaims(
  report: SiteRagReport,
  _anchors: DocAnchor[],
  documents: SiteDocument[] = [],
): ClaimAnchorBinding[] {
  const text = reportFullText(report);
  const lines = text.split('\n');
  const bindings: ClaimAnchorBinding[] = [];
  const docsById = new Map(documents.map((d) => [d.id, d]));

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const lineNum = i + 1;

    DOC_URL_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = DOC_URL_RE.exec(line)) !== null) {
      const parsed = parseDocCitationUrl(m[0].replace(/[.,;:!?]+$/, ''));
      const ctxStart = Math.max(0, m.index - 120);
      const claimText = line.slice(ctxStart, m.index).trim();
      bindings.push({
        claimText,
        docId: parsed?.docId,
        anchor: parsed ?? undefined,
        lineNumber: lineNum,
        bindMethod: 'doc-url',
      });
    }

    AT_DOC_RE.lastIndex = 0;
    while ((m = AT_DOC_RE.exec(line)) !== null) {
      const docId = m[1]!.trim();
      const ctxStart = Math.max(0, m.index - 120);
      const claimText = line.slice(ctxStart, m.index).trim();
      bindings.push({
        claimText,
        docId,
        fileName: docsById.get(docId)?.fileName,
        lineNumber: lineNum,
        bindMethod: 'at-doc-tag',
      });
    }

    PER_FILENAME_RE.lastIndex = 0;
    while ((m = PER_FILENAME_RE.exec(line)) !== null) {
      const filename = m[1]!.trim();
      const matched = matchDocByFilename(filename, documents);
      const ctxStart = Math.max(0, m.index - 120);
      const claimText = line.slice(ctxStart, m.index + m[0].length).trim();
      // An anchor for another document or another sentence cannot establish
      // this attribution. Keep invalid matching anchors for the doc-url
      // verifier to reject; never substitute page/character coordinates.
      let sentenceStart = 0;
      let sentenceEnd = line.length;
      for (const boundary of line.matchAll(/[.!?](?=\s|$)/g)) {
        if (boundary.index < m.index) sentenceStart = boundary.index + 1;
        else if (boundary.index >= m.index + m[0].length) {
          sentenceEnd = boundary.index + 1;
          break;
        }
      }
      const sentence = line.slice(sentenceStart, sentenceEnd);
      const matchingAnchor = matched
        ? [...sentence.matchAll(new RegExp(DOC_URL_RE.source, 'gi'))]
          .map(url => parseDocCitationUrl(url[0].replace(/[.,;:!?]+$/, '')))
          .find(anchor => anchor?.docId === matched.id)
        : undefined;
      const matchingTag = matched && [...sentence.matchAll(new RegExp(AT_DOC_RE.source, 'gi'))]
        .some(tag => tag[1]?.trim() === matched.id);
      bindings.push({
        claimText,
        docId: matched?.id,
        fileName: filename,
        anchor: matchingAnchor ?? undefined,
        lineNumber: lineNum,
        bindMethod: matchingAnchor || matchingTag ? 'per-filename' : 'unbound',
      });
    }
  }

  return bindings;
}

/** Verify doc-derived claims against uploaded document anchors. */
export function verifyDocProvenance(
  report: SiteRagReport,
  evidence: DocProvenanceEvidence,
): ClaimProvenanceFinding[] {
  const findings: ClaimProvenanceFinding[] = [];
  const docs = evidence.uploadedDocs ?? [];
  const docsById = new Map(docs.map((d) => [d.id, d]));
  const bindings = findDocDerivedClaims(report, evidence.anchors, docs);

  for (const b of bindings) {
    if (b.bindMethod === 'doc-url' && b.anchor) {
      if (b.anchor.docId.startsWith('sim/')) {
        continue;
      }
      const doc = docsById.get(b.anchor.docId);
      if (!doc) {
        findings.push({
          kind: 'PHANTOM_DOC',
          severity: 'block',
          docId: b.anchor.docId,
          lineNumber: b.lineNumber,
          message: `doc:// citation references missing document ${b.anchor.docId}`,
          claimText: b.claimText,
          anchor: b.anchor,
        });
        continue;
      }
      // P1.5 — chunks are anchored into the WHOLE body; the site-record copy
      // carries only the preview, so validate against the captured length.
      const textLen = Math.max(
        (doc.textContent ?? '').length,
        doc.fullExtractedText?.length ?? 0,
        doc.extractedCharsKept ?? 0,
      );
      if (
        b.anchor.charStart > textLen
        || b.anchor.charEnd > textLen
        || b.anchor.charEnd <= b.anchor.charStart
      ) {
        findings.push({
          kind: 'ORPHAN_CHAR_RANGE',
          severity: 'block',
          docId: b.anchor.docId,
          fileName: doc.fileName,
          lineNumber: b.lineNumber,
          message: `doc:// char range ${b.anchor.charStart}-${b.anchor.charEnd} out of bounds for ${doc.fileName} (${textLen} chars)`,
          claimText: b.claimText,
          anchor: b.anchor,
        });
        continue;
      }
      const snippet = sliceAtAnchor(doc, b.anchor);
      if (b.claimText.length > 20 && tokenOverlap(b.claimText, snippet) < 0.15) {
        findings.push({
          kind: 'SNIPPET_MISMATCH',
          severity: 'warn',
          docId: b.anchor.docId,
          fileName: doc.fileName,
          lineNumber: b.lineNumber,
          message: `Claim text weakly overlaps doc:// anchor in ${doc.fileName}`,
          claimText: b.claimText,
          anchor: b.anchor,
          expectedSnippet: snippet.slice(0, 120),
        });
      }
      continue;
    }

    if (b.bindMethod === 'at-doc-tag' && b.docId) {
      if (!docsById.has(b.docId)) {
        findings.push({
          kind: 'PHANTOM_DOC',
          severity: 'block',
          docId: b.docId,
          lineNumber: b.lineNumber,
          message: `@doc(${b.docId}) references missing document`,
          claimText: b.claimText,
        });
      }
      continue;
    }

    if (b.bindMethod === 'unbound' && b.fileName) {
      const matched = b.docId ? docsById.get(b.docId) : matchDocByFilename(b.fileName, docs);
      if (!matched) {
        findings.push({
          kind: 'PHANTOM_DOC',
          severity: 'block',
          fileName: b.fileName,
          lineNumber: b.lineNumber,
          message: `Cited document "${b.fileName}" not found in site Documents tab`,
          claimText: b.claimText,
        });
      } else {
        findings.push({
          kind: 'UNANCHORED_DOC_CLAIM',
          severity: 'warn',
          docId: matched.id,
          fileName: matched.fileName,
          lineNumber: b.lineNumber,
          message: `Tier-3 doc claim (per "${b.fileName}") lacks doc:// char anchor — add doc://${matched.id}#page=&start=&end=`,
          claimText: b.claimText,
        });
      }
      continue;
    }

    if (b.bindMethod === 'per-filename' && b.docId && !b.anchor) {
      findings.push({
        kind: 'MISSING_DOC_CITATION',
        severity: 'warn',
        docId: b.docId,
        fileName: b.fileName,
        lineNumber: b.lineNumber,
        message: `Document "${b.fileName}" cited inline but missing doc:// char-range anchor on same line`,
        claimText: b.claimText,
      });
    }
  }

  return findings;
}

export function applyDocProvenanceGate(
  report: SiteRagReport,
  evidence: DocProvenanceEvidence,
): SiteRagReport {
  const findings = verifyDocProvenance(report, evidence);
  if (findings.length === 0) return report;
  const blocks = findings.filter((f) => f.severity === 'block').length;
  const warns = findings.filter((f) => f.severity === 'warn').length;
  console.info('[R-70 doc provenance]', {
    reportId: report.id,
    blocks,
    warns,
    kinds: [...new Set(findings.map((f) => f.kind))],
  });
  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      docProvenanceFindings: findings.map((f) => ({
        kind: f.kind,
        severity: f.severity,
        docId: f.docId,
        fileName: f.fileName,
        lineNumber: f.lineNumber,
        message: f.message,
        claimText: f.claimText,
        anchor: f.anchor
          ? {
            docId: f.anchor.docId,
            page: f.anchor.page,
            charStart: f.anchor.charStart,
            charEnd: f.anchor.charEnd,
          }
          : undefined,
      })),
    },
  };
}
